import {AssistantLogo} from "../ChatMessages/ChatMessages.styled";
import {brand} from "../../../theme/brand";

import React, {useCallback, useState} from "react";
import {AssistantVariant, Message} from "../../../models/models";
import {useTranslation} from "react-i18next";
import {Menu, MenuItem, Tooltip} from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {ReactComponent as DocumentDownloadIcon} from '../../../assets/icons/document-download.svg';

import {formatMessageTimestamp} from "../../../utils/format-date";
import {AvatarBlock} from "../ChatInput/ChatInput.styled";
import {
    MessageAttachmentsWrapper,
    MessageSystemAuthor,
    MessageSystemHeader,
    MessageSystemHeaderIcons,
    MessageTimestamp,
    MessageYou,
    MessageYouAuthor,
    MessageYouText,
    StyledButton,
} from "./MessagesList.styled";
import {
    AttachmentFooter,
    DocumentAttachmentWrapper,
    DocumentInfo,
    DocumentName,
    StyledDownloadButton
} from "../MessageAttachments/MessageAttachments.styled";
import {theme, tokens} from "../../../theme";
import AssistantMessage from "../AssistantMessage";
import {CurrentUser} from "../../../models/users.models";

interface MessagesListProps {
    user: CurrentUser | null;
    messages: Message[] | undefined;
    variants: AssistantVariant[] | undefined;
    isGenerating: boolean | undefined;
    onRetry: (() => void) | undefined;
    onRetryNewChat: (() => void) | undefined;
    canRetry: boolean | undefined;
    onActivateVariant: ((assistantMessageId: number) => void) | undefined;
    canNavigateVariants: boolean | undefined;
    downloadDocumentForId: (documentId: number, documentName: string) => void;
    exportToPdf?: () => Promise<void>;
}

const MessagesList = React.memo(
    function MessagesList({
            user,
            messages,
            variants,
            isGenerating,
            onRetry,
            onRetryNewChat,
            canRetry,
            onActivateVariant,
            canNavigateVariants,
            downloadDocumentForId,
            exportToPdf,
        }: MessagesListProps
    ) {
        const {t} = useTranslation();

        const [retryAnchorEl, setRetryAnchorEl] = useState<null | HTMLElement>(null);
        const isRetryMenuOpen = Boolean(retryAnchorEl);
        const [openCitation, setOpenCitation] = useState<{ messageIndex: number | null, citationIndex: number | null }>({ messageIndex: null, citationIndex: null });

        const openRetryMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
            if (!canRetry) return;
            setRetryAnchorEl(e.currentTarget);
        };
        const closeRetryMenu = () => setRetryAnchorEl(null);

        // TODO: if citation open one per message, leave if open one per convo
        const toggleCitation = useCallback((messageIndex: number, citationIndex: number) => {
            setOpenCitation(prev =>
                prev.messageIndex === messageIndex && prev.citationIndex === citationIndex
                    ? { messageIndex: null, citationIndex: null}
                    : { messageIndex, citationIndex }
            );
        }, []);

        const copyToClipboard = useCallback((text: string | React.ReactNode | (string | React.ReactNode)[]): void => {
            let textToCopy = '';
            if (Array.isArray(text)) {
                textToCopy = text.map(item =>
                    typeof item === 'string'
                        ? item
                        : (item &&
                        typeof (item as any).props === 'object' &&
                        typeof (item as any).props.children === 'string'
                            ? (item as any).props.children
                            : '')
                ).join('');
            } else if (typeof text === 'string') {
                textToCopy = text;
            }
            if (textToCopy) {
                void navigator.clipboard.writeText(textToCopy);
            }
        }, []);

        return (
            <>
                {messages?.map((message, index) => {
                    const messageKey = message.id || `msg-index-${index}`;
                    const isLastAssistant = message.isSystem && index === (messages?.length ?? 1) - 1;
                    const hasVariants =
                        isLastAssistant &&
                        !message.isLoading &&
                        !isGenerating &&
                        Array.isArray(variants) &&
                        variants.length > 0;

                    const activeVariant = hasVariants
                        ? (variants.find(v => v.active) ?? variants[0])
                        : undefined;

                    const displayedText =
                        (hasVariants && activeVariant?.content)
                            ? activeVariant.content
                            : message.text;

                    const displayedThinking = (hasVariants && activeVariant?.thinking)
                        ? activeVariant.thinking
                        : (message.thinking || "");

                    const displayedDocs =
                        (hasVariants && activeVariant?.documents && activeVariant.documents.length > 0)
                            ? activeVariant.documents
                            : (message.documents || []);

                    const displayedCitations =
                        (hasVariants && activeVariant?.citations)
                            ? activeVariant.citations
                            : (message.citations || []);

                    const currentVariantIndex =
                        hasVariants && activeVariant
                            ? variants.findIndex(v => v.id === activeVariant.id)
                            : -1;

                    const totalVariants = hasVariants ? variants.length : 0;

                    const goPrevVariant = () => {
                        if (!hasVariants || !onActivateVariant || currentVariantIndex <= 0) return;
                        const prevId = variants[currentVariantIndex - 1].id;
                        onActivateVariant(prevId);
                    };

                    const goNextVariant = () => {
                        if (!hasVariants || !onActivateVariant || currentVariantIndex === -1 || currentVariantIndex >= totalVariants - 1) return;
                        const nextId = variants[currentVariantIndex + 1].id;
                        onActivateVariant(nextId);
                    };

                    return (
                        <div key={messageKey} style={!message.isSystem ? {marginBottom: '0px'} : {}}>
                            {message.isSystem ? (
                                <>
                                    <AssistantMessage
                                        hasAttachment={!!(displayedDocs && displayedDocs.length > 0)}
                                        isLoading={!!message.isLoading}
                                        isGenerating={!!isGenerating && isLastAssistant}
                                        isError={!!message.isError}
                                        errorText={message.text}
                                        thinkingText={displayedThinking}
                                        responseText={displayedText}
                                        citations={displayedCitations || []}
                                        openCitationIndex={openCitation.messageIndex === index ? openCitation.citationIndex : null}
                                        toggleCitation={(citationIndex) => toggleCitation(index, citationIndex)}
                                    />

                                    <MessageSystemHeader>
                                        <MessageSystemAuthor>
                                            <AssistantLogo src={brand.logo} alt="Logo"/>
                                        </MessageSystemAuthor>

                                        {!message.isLoading && !message.isError && (
                                            <MessageSystemHeaderIcons>
                                                <Tooltip title={t('chatMessages.copy')}>
                                                    <StyledButton onClick={() => copyToClipboard(displayedText)}>
                                                        <ContentCopyIcon sx={{ color: theme.palette.secondary.main, fontSize: '18px' }} />
                                                    </StyledButton>
                                                </Tooltip>

                                                {isLastAssistant && (
                                                    <>
                                                        <Tooltip title={t('chat.downloadConversation')}>
                                                            <StyledButton onClick={exportToPdf}>
                                                                <DocumentDownloadIcon style={{color: theme.palette.secondary.main, fontSize: tokens.fontSize.base }} />
                                                            </StyledButton>
                                                        </Tooltip>

                                                        <Tooltip title={t('chatMessages.retry')}>
                                                                <span>
                                                                    <StyledButton
                                                                        onClick={openRetryMenu}
                                                                        disabled={!canRetry}
                                                                        aria-haspopup="true"
                                                                        aria-expanded={isRetryMenuOpen ? 'true' : undefined}
                                                                        aria-controls={isRetryMenuOpen ? 'retry-menu' : undefined}
                                                                    >
                                                                        <RefreshIcon sx={{ color: theme.palette.secondary.main }} />
                                                                    </StyledButton>
                                                                </span>
                                                        </Tooltip>
                                                        <Menu
                                                            id="retry-menu"
                                                            anchorEl={retryAnchorEl}
                                                            open={isRetryMenuOpen}
                                                            onClose={closeRetryMenu}
                                                            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                                                            transformOrigin={{vertical: 'top', horizontal: 'right'}}
                                                        >
                                                            <MenuItem
                                                                onClick={() => {
                                                                    closeRetryMenu();
                                                                    onRetry?.();
                                                                }}
                                                            >
                                                                {t('chatMessages.retrySame')}
                                                            </MenuItem>
                                                            <MenuItem
                                                                onClick={() => {
                                                                    closeRetryMenu();
                                                                    onRetryNewChat?.();
                                                                }}
                                                            >
                                                                {t('chatMessages.retryNewChat')}
                                                            </MenuItem>
                                                        </Menu>
                                                    </>
                                                )}
                                            </MessageSystemHeaderIcons>
                                        )}
                                    </MessageSystemHeader>

                                    <MessageAttachmentsWrapper>
                                        {hasVariants && totalVariants > 1 && (
                                            <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                                                <Tooltip title={t('chatMessages.prevVariant')}>
                                                    <span>
                                                        <StyledButton
                                                            onClick={goPrevVariant}
                                                            disabled={!canNavigateVariants || currentVariantIndex <= 0}
                                                            aria-label={t('chatMessages.prevVariant') || ''}
                                                        >
                                                            <ChevronLeftIcon sx={{ color: theme.palette.primary.main, fontSize: '14px' }} />
                                                        </StyledButton>
                                                    </span>
                                                </Tooltip>
                                                <span
                                                    style={{color: '#1d2a41', fontSize: 12}}
                                                    title={t('chatMessages.variantCounter') || ''}
                                                    aria-label={t('chatMessages.variantCounter') || ''}
                                                >
                                                    {(currentVariantIndex + 1)}/{totalVariants}
                                                </span>
                                                <Tooltip title={t('chatMessages.nextVariant')}>
                                                    <span>
                                                        <StyledButton
                                                            onClick={goNextVariant}
                                                            disabled={
                                                                !canNavigateVariants ||
                                                                currentVariantIndex === -1 ||
                                                                currentVariantIndex >= totalVariants - 1
                                                            }
                                                            aria-label={t('chatMessages.nextVariant') || ''}
                                                        >
                                                            <ChevronRightIcon sx={{ color: theme.palette.primary.main, fontSize: '14px' }} />
                                                        </StyledButton>
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        )}

                                        {displayedDocs && displayedDocs.length > 0 ? (
                                            <>
                                                {displayedDocs.map((doc, docIndex) => (
                                                    <DocumentAttachmentWrapper key={doc.documentId && doc.documentId !== -1 ? `doc-${doc.documentId}` : `doc-idx-${docIndex}`} $show={true}>
                                                        <AttachmentFooter>
                                                            <DocumentInfo>
                                                                <DocumentName>
                                                                    {t('chatMessages.source') + doc.documentName}
                                                                </DocumentName>
                                                            </DocumentInfo>
                                                            {(doc.documentId && doc.documentId !== -1) && (
                                                                <Tooltip title={t('chatMessages.download')}>
                                                                    <StyledDownloadButton
                                                                        variant="text"
                                                                        color="primary"
                                                                        size="small"
                                                                        startIcon={<DownloadIcon/>}
                                                                        onClick={() => {
                                                                            if (doc.documentId && doc.documentId !== -1 && doc.documentName) {
                                                                                downloadDocumentForId(
                                                                                    doc.documentId,
                                                                                    doc.documentName
                                                                                );
                                                                            }
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </AttachmentFooter>
                                                    </DocumentAttachmentWrapper>
                                                ))}
                                            </>
                                        ) : null}
                                        {message.timestamp && !message.isLoading && !message.isError && (
                                            <MessageTimestamp
                                                style={
                                                    !(displayedDocs && displayedDocs.length > 0)
                                                        ? {marginLeft: 'auto'}
                                                        : {}
                                                }
                                            >
                                                {formatMessageTimestamp(message.timestamp)}
                                            </MessageTimestamp>
                                        )}
                                    </MessageAttachmentsWrapper>
                                </>
                            ) : (
                                <>
                                    <MessageYou $hasAttachment={!!(message.documents && message.documents.length > 0)}>
                                        <MessageYouAuthor>
                                            {user?.avatarBase64 ? (
                                                <img
                                                    style={{
                                                        width: '35px',
                                                        height: '35px',
                                                        borderRadius: '50%',
                                                    }}
                                                    src={user.avatarBase64}
                                                    alt="User Avatar"
                                                />
                                            ) : (
                                                <AvatarBlock>
                                                    {(() => {
                                                        const firstName = user?.firstName || '';
                                                        const lastName = user?.lastName || '';
                                                        if (firstName && lastName) {
                                                            return `${firstName.charAt(0)}${lastName.charAt(0)}`;
                                                        } else if (firstName) {
                                                            return firstName.charAt(0);
                                                        } else if (user?.email) {
                                                            return user.email.charAt(0).toUpperCase();
                                                        } else {
                                                            return '?';
                                                        }
                                                    })()}
                                                </AvatarBlock>
                                            )}
                                            <span>
                                                {(() => {
                                                    const firstName = user?.firstName || '';
                                                    const lastName = user?.lastName || '';

                                                    if (firstName && lastName) {
                                                        return `${firstName} ${lastName}`;
                                                    } else if (firstName) {
                                                        return firstName;
                                                    } else if (lastName) {
                                                        return lastName;
                                                    } else {
                                                        return t('chatMessages.system');
                                                    }
                                                })()}
                                            </span>
                                        </MessageYouAuthor>

                                        <MessageYouText>
                                            {message.text}
                                        </MessageYouText>

                                    </MessageYou>

                                    <MessageAttachmentsWrapper>
                                        {// TODO : AI-952 needs a fix
                                            message.documents && message.documents.length > 0 ? (
                                            <>
                                                {message.documents.map((doc, docIndex) => (
                                                    <DocumentAttachmentWrapper key={doc.documentId && doc.documentId !== -1 ? `doc-u-${doc.documentId}` : `doc-u-idx-${docIndex}`}
                                                                               $show={true}
                                                    >
                                                        <AttachmentFooter>
                                                            <DocumentInfo>
                                                                <DocumentName>{doc.documentName}</DocumentName>
                                                            </DocumentInfo>
                                                            {(doc.documentId && doc.documentId !== -1) && (
                                                                <Tooltip title={t('chatMessages.download')}>
                                                                    <StyledDownloadButton
                                                                        variant="text"
                                                                        color="primary"
                                                                        size="small"
                                                                        startIcon={<DownloadIcon/>}
                                                                        onClick={() => {
                                                                            if (doc.documentId && doc.documentId !== -1 && doc.documentName) {
                                                                                downloadDocumentForId(
                                                                                    doc.documentId,
                                                                                    doc.documentName
                                                                                );
                                                                            }
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </AttachmentFooter>
                                                    </DocumentAttachmentWrapper>
                                                ))}
                                            </>
                                        ) : null}

                                        {message.timestamp && (
                                            <MessageTimestamp
                                                style={
                                                    !(message.documents && message.documents.length > 0)
                                                        ? {marginLeft: 'auto'}
                                                        : {}
                                                }
                                            >
                                                {formatMessageTimestamp(message.timestamp)}
                                            </MessageTimestamp>
                                        )}
                                    </MessageAttachmentsWrapper>
                                </>
                            )}
                        </div>
                    );
                })}

            </>
        );
    },
);

export default MessagesList;
